import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  AgentDefinitionResponseSchema,
  AgentInputFieldDefSchema,
  AgentModelConfigSchema,
  AgentOutputFieldDefSchema,
  AgentToolDefSchema,
} from "./agent-definitions.contract";
import {
  SopBundleDroppedReferenceSchema,
  SopBundleSchema,
} from "./sop-bundle.contract";
import { parseVersionedBundle } from "./shared/parseVersionedBundle";

/**
 * The portable JSON form of an agent definition plus every procedure it uses — what the agent
 * page's "Export" writes and "Import" reads. Lets an agent be tuned locally and replayed into
 * staging/prod as one file.
 *
 * Procedures ride along as ordinary `.sop.json` bundles (`sop-bundle.contract.ts`), each tagged
 * with the source procedure id + version it was exported from. Those ids are what the agent's
 * `config.procedures.sopIds` and composed `sop_ref` steps point at inside the file; import
 * re-points them at the procedures it creates or versions in the target organization.
 *
 * Deliberately NOT in the file:
 * - lineage settings that describe one environment's wiring rather than the agent's behavior
 *   (`isActive`, namespace, resources, schedules, permissions);
 * - inline secrets in `config` (`password`, `vmPassword`) — exported blank; import keeps the
 *   target agent's own value.
 *
 * Bump `AGENT_BUNDLE_FORMAT_VERSION` only for a change an older reader cannot tolerate.
 */
export const AGENT_BUNDLE_FORMAT_VERSION = 1 as const;

/** The versioned behavior of the agent, plus the lineage labels a new import is created with. */
export const AgentBundleAgentSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().nullish(),
  templateType: z.string().nullish(),
  systemPrompt: z.string(),
  modelConfig: AgentModelConfigSchema,
  config: z.record(z.string(), z.unknown()),
  inputFields: z.array(AgentInputFieldDefSchema),
  outputFields: z.array(AgentOutputFieldDefSchema),
  tools: z.array(AgentToolDefSchema),
});

export type AgentBundleAgent = z.infer<typeof AgentBundleAgentSchema>;

/** One procedure the agent uses, directly or through composition / fallback login. */
export const AgentBundleProcedureSchema = z.object({
  /** The procedure's id in the exporting organization — what the file's references name. */
  sopId: z.string().min(1),
  /** The exported version. A composed step pins a version, so one procedure can appear twice. */
  version: z.number().int().positive(),
  bundle: SopBundleSchema,
});

export type AgentBundleProcedure = z.infer<typeof AgentBundleProcedureSchema>;

/** Provenance, for humans reading a diff. Never used to resolve anything. */
export const AgentBundleSourceSchema = z.object({
  organizationId: z.string().optional(),
  agentDefinitionId: z.string().optional(),
  versionId: z.string().optional(),
  version: z.number().optional(),
  exportedAt: z.string().optional(),
});

/** Two entries for the same exported version would leave a reference with two meanings. */
function duplicateProcedureKeys(
  procedures: readonly { sopId: string; version: number }[],
): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const { sopId, version } of procedures) {
    const key = `${sopId}@${version}`;
    if (seen.has(key)) duplicates.add(key);
    seen.add(key);
  }
  return [...duplicates];
}

export const AgentBundleSchema = z.object({
  formatVersion: z.literal(AGENT_BUNDLE_FORMAT_VERSION),
  source: AgentBundleSourceSchema.optional(),
  agent: AgentBundleAgentSchema,
  procedures: z
    .array(AgentBundleProcedureSchema)
    .default([])
    .superRefine((procedures, ctx) => {
      const duplicates = duplicateProcedureKeys(procedures);
      if (duplicates.length > 0) {
        ctx.addIssue({
          code: "custom",
          message: `duplicate procedure: ${duplicates.join(", ")}`,
        });
      }
    }),
});

export type AgentBundle = z.infer<typeof AgentBundleSchema>;

/** Parse untrusted JSON into an agent bundle, or a flat list of readable errors. */
export function parseAgentBundle(
  raw: unknown,
): { ok: true; bundle: AgentBundle } | { ok: false; errors: string[] } {
  // The likeliest wrong file is a procedure export — say so instead of "agent: Required".
  if (
    typeof raw === "object" &&
    raw !== null &&
    !("agent" in raw) &&
    "procedure" in raw
  ) {
    return {
      ok: false,
      errors: [
        "This is a procedure file. Import it from the Procedures page instead.",
      ],
    };
  }
  return parseVersionedBundle(raw, {
    schema: AgentBundleSchema,
    formatVersion: AGENT_BUNDLE_FORMAT_VERSION,
    label: "agent",
  });
}

export const ExportAgentBundleInputSchema = z.object({
  agentDefinitionId: z.uuid(),
  /** The version to export; the published version when omitted. */
  versionId: z.uuid().optional(),
});

export const ImportAgentBundleInputSchema = z.object({
  bundle: AgentBundleSchema,
  /** Add the file as a new version of this agent. Omitted → create a new (inactive) agent. */
  agentDefinitionId: z.uuid().optional(),
  /** Publish the imported version so new runs use it. Ignored for a new agent (always v1). */
  publish: z.boolean().optional().default(true),
});

export type ImportAgentBundleInput = z.input<
  typeof ImportAgentBundleInputSchema
>;

/** What import did with one procedure in the file. */
export const ImportedProcedureOutcomeSchema = z.enum([
  /** No procedure with this name existed — created at v1. */
  "created",
  /** A same-named procedure existed — the file's steps were published as its next version. */
  "new_version",
  /** A same-named procedure's published version already matched the file. */
  "unchanged",
]);

export const ImportedProcedureSchema = z.object({
  sopId: z.string(),
  name: z.string(),
  title: z.string(),
  version: z.number().int(),
  outcome: ImportedProcedureOutcomeSchema,
  /** Org-scoped login references from the file that don't exist here and were not applied. */
  droppedReferences: z.array(SopBundleDroppedReferenceSchema),
});

export type ImportedProcedure = z.infer<typeof ImportedProcedureSchema>;

export const ImportAgentBundleResponseSchema = z.object({
  definition: AgentDefinitionResponseSchema,
  /** The version the import created. */
  versionId: z.uuid(),
  version: z.number().int(),
  published: z.boolean(),
  /** True when the import created a new agent rather than versioning an existing one. */
  created: z.boolean(),
  procedures: z.array(ImportedProcedureSchema),
  /** Config paths whose credential reference didn't exist here and had no local value to keep. */
  droppedConfigReferences: z.array(z.string()),
});

export type ImportAgentBundleResponse = z.infer<
  typeof ImportAgentBundleResponseSchema
>;

export const agentBundleContracts = {
  /** The agent version as a portable bundle, with every procedure it uses. */
  exportBundle: oc
    .input(ExportAgentBundleInputSchema)
    .output(AgentBundleSchema)
    .route({
      method: "GET",
      summary: "Export an agent as a bundle",
      description:
        "Returns one agent version as a portable bundle that includes every procedure it uses, for import into another organization or environment.",
    }),

  /** Import a bundle as a new version of an agent (or a new agent), versioning its procedures. */
  importBundle: oc
    .input(ImportAgentBundleInputSchema)
    .output(ImportAgentBundleResponseSchema)
    .route({
      method: "POST",
      summary: "Import an agent bundle",
      description:
        "Adds a bundle as a new version of an existing agent, published by default, or creates a new inactive agent; its procedures are versioned too.",
    }),
};
